/**
 * PHASE 5: useOfflineSync Hook
 * React hook for offline sync status and actions
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  initOfflineDb,
  getQueueStats,
  listQueueItems,
  clearCompletedQueueItems,
  subscribeToConnectivity,
  getNetworkStatus,
  initNetworkMonitoring,
  replayPendingQueue
} from '@/lib/offline';

/**
 * Main hook for offline sync status
 * @returns {Object} Sync state and actions
 */
export function useOfflineSync() {
  const [isOnline, setIsOnline] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [failedCount, setFailedCount] = useState(0);
  const [conflictCount, setConflictCount] = useState(0);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  const [queueItems, setQueueItems] = useState([]);
  
  const pollIntervalRef = useRef(null);
  const initializedRef = useRef(false);

  // Refresh queue statistics
  const refreshQueueStats = useCallback(async () => {
    try {
      const stats = await getQueueStats();
      setPendingCount(stats.pending + stats.syncing);
      setFailedCount(stats.failed);
      setConflictCount(stats.conflict);
      
      // Load queue items for display
      const items = await listQueueItems();
      setQueueItems(items.sort((a, b) => b.createdAt - a.createdAt));
      
    } catch (error) {
      console.error('[useOfflineSync] Failed to refresh stats:', error);
    }
  }, []);

  // Replay the queue now (no guards): the reconnect listener calls this directly,
  // because it fires before the isOnline it just set has rendered.
  const runSync = useCallback(async () => {
    setIsSyncing(true);
    
    try {
      const results = await replayPendingQueue({
        limit: 50,
        onProgress: () => {
          // Refresh stats during sync
          refreshQueueStats();
        }
      });
      
      setLastSyncedAt(Date.now());
      await refreshQueueStats();
      
      return { success: true, results };
      
    } catch (error) {
      console.error('[useOfflineSync] Sync error:', error);
      return { success: false, reason: 'error', error };
    } finally {
      setIsSyncing(false);
    }
  }, [refreshQueueStats]);

  // Initialize offline system
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    
    const init = async () => {
      try {
        // Initialize database
        await initOfflineDb();
        
        // Initialize network monitoring
        initNetworkMonitoring();
        
        // Get initial status
        const status = getNetworkStatus();
        setIsOnline(status.online);
        
        // Load initial queue stats
        await refreshQueueStats();
        
      } catch (error) {
        console.error('[useOfflineSync] Init error:', error);
      }
    };
    
    init();
    
    // Subscribe to connectivity changes
    const unsubscribe = subscribeToConnectivity((status) => {
      setIsOnline(status.online);
      
      // Auto-sync when coming back online
      if (status.online) {
        runSync();
      }
    });
    
    // Poll queue stats when tab is active
    const startPolling = () => {
      if (pollIntervalRef.current) return;
      pollIntervalRef.current = setInterval(() => {
        if (document.visibilityState === 'visible') {
          refreshQueueStats();
        }
      }, 5000);
    };
    
    const stopPolling = () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
    
    // Start polling when visible
    if (document.visibilityState === 'visible') {
      startPolling();
    }
    
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        startPolling();
        refreshQueueStats();
      } else {
        stopPolling();
      }
    });
    
    return () => {
      unsubscribe();
      stopPolling();
    };
  }, [refreshQueueStats, runSync]);

  // Trigger manual sync
  const syncNow = useCallback(async () => {
    if (isSyncing) return { success: false, reason: 'already_syncing' };
    if (!isOnline) return { success: false, reason: 'offline' };
    return runSync();
  }, [isSyncing, isOnline, runSync]);

  // Clear completed items
  const clearCompleted = useCallback(async () => {
    try {
      await clearCompletedQueueItems();
      await refreshQueueStats();
    } catch (error) {
      console.error('[useOfflineSync] Clear error:', error);
    }
  }, [refreshQueueStats]);

  // Compute status label
  const syncStatusLabel = (() => {
    if (!isOnline) return 'Offline';
    if (isSyncing) return 'Syncing...';
    if (conflictCount > 0) return `${conflictCount} conflict${conflictCount > 1 ? 's' : ''}`;
    if (failedCount > 0) return `${failedCount} failed`;
    if (pendingCount > 0) return `${pendingCount} pending`;
    return 'Online';
  })();

  return {
    isOnline,
    isSyncing,
    pendingCount,
    failedCount,
    conflictCount,
    lastSyncedAt,
    queueItems,
    syncStatusLabel,
    syncNow,
    clearCompleted,
    refreshStats: refreshQueueStats
  };
}

export default useOfflineSync;
